import { supabase } from "../../lib/supabaseClient";
import { mapProfile } from "./mappers";

function requireClient() {
  if (!supabase) throw new Error("Supabase is not configured.");
  return supabase;
}

function sameId(a, b) {
  return a != null && b != null && String(a) === String(b);
}

function mapMessage(msg) {
  return {
    id: msg.id,
    conversationId: msg.conversation_id,
    senderId: msg.sender_id,
    content: msg.content,
    timestamp: msg.created_at,
    createdAt: msg.created_at,
  };
}

async function buildConversation(conv, currentUserId) {
  const otherId = sameId(conv.participant_a, currentUserId)
    ? conv.participant_b
    : conv.participant_a;

  const { data: profile } = await requireClient()
    .from("profiles")
    .select("*")
    .eq("id", otherId)
    .maybeSingle();

  const { data: messages } = await requireClient()
    .from("messages")
    .select("*")
    .eq("conversation_id", conv.id)
    .order("created_at", { ascending: true })
    .limit(100);

  const mappedMessages = (messages || []).map(mapMessage);
  const last = mappedMessages[mappedMessages.length - 1];

  const mappedUser = mapProfile(profile);

  return {
    conversationId: conv.id,
    userId: otherId,
    otherUserId: otherId,
    user: mappedUser,
    username: mappedUser?.firstName ?? null,
    userName: mappedUser?.firstName ?? null,
    profileImageUri: mappedUser?.profileImageUri ?? null,
    messages: mappedMessages,
    lastMessage: last?.content ?? conv.last_message ?? "",
    lastMessageTime: last?.timestamp ?? conv.last_message_at ?? conv.created_at ?? null,
  };
}

function sortConversations(list) {
  return [...list].sort((a, b) => {
    const ta = new Date(a.lastMessageTime || 0).getTime();
    const tb = new Date(b.lastMessageTime || 0).getTime();
    return (Number.isFinite(tb) ? tb : 0) - (Number.isFinite(ta) ? ta : 0);
  });
}

export async function GetUserConversations(userId) {
  const uid = String(userId);
  const { data, error } = await requireClient()
    .from("conversations")
    .select("*")
    .or(`participant_a.eq.${uid},participant_b.eq.${uid}`)
    .order("last_message_at", { ascending: false, nullsFirst: false })
    .order("created_at", { ascending: false });
  if (error) throw error;

  const rows = data || [];
  const built = await Promise.all(
    rows.map(async (c) => {
      try {
        return await buildConversation(c, uid);
      } catch (err) {
        console.warn("GetUserConversations: skipped conversation", c?.id, err);
        return null;
      }
    })
  );
  return sortConversations(built.filter(Boolean));
}

/** Load a single conversation for the inbox (e.g. deep-link /messages/:id). */
export async function GetBuiltConversationById(conversationId, currentUserId) {
  if (!conversationId || !currentUserId) return null;
  const { data, error } = await requireClient()
    .from("conversations")
    .select("*")
    .eq("id", conversationId)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;

  const uid = String(currentUserId);
  if (!sameId(data.participant_a, uid) && !sameId(data.participant_b, uid)) {
    return null;
  }
  return buildConversation(data, uid);
}

export async function SendMessage({ conversationId, senderId, content }) {
  const { data, error } = await requireClient()
    .from("messages")
    .insert({
      conversation_id: conversationId,
      sender_id: senderId,
      content,
    })
    .select("*")
    .single();
  if (error) throw error;

  await requireClient()
    .from("conversations")
    .update({ last_message: content, last_message_at: new Date().toISOString() })
    .eq("id", conversationId);

  // Recipient push is handled by DB trigger (push_on_message_insert). Never notify the sender.
  return mapMessage(data);
}

export async function markConversationRead(conversationId, userId) {
  const { data: conv } = await requireClient()
    .from("conversations")
    .select("*")
    .eq("id", conversationId)
    .maybeSingle();
  if (!conv) return;

  const field = sameId(conv.participant_a, userId)
    ? "participant_a_last_read_at"
    : "participant_b_last_read_at";
  await requireClient()
    .from("conversations")
    .update({ [field]: new Date().toISOString() })
    .eq("id", conversationId);
}

export function subscribeToMessages(onInsert) {
  const client = requireClient();
  return client
    .channel("public:messages")
    .on(
      "postgres_changes",
      { event: "INSERT", schema: "public", table: "messages" },
      (payload) => onInsert(payload.new)
    )
    .subscribe();
}

export function subscribeToConversations(userId, onChange) {
  const client = requireClient();
  const uid = String(userId);
  return client
    .channel(`conversations:${uid}`)
    .on(
      "postgres_changes",
      { event: "*", schema: "public", table: "conversations" },
      (payload) => {
        const row = payload.new || payload.old;
        if (
          row &&
          !sameId(row.participant_a, uid) &&
          !sameId(row.participant_b, uid)
        ) {
          return;
        }
        onChange();
      }
    )
    .subscribe();
}

export async function GetConversationById(conversationId) {
  const { data, error } = await requireClient()
    .from("conversations")
    .select("*")
    .eq("id", conversationId)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function GetMessagesByConversationId(conversationId) {
  const { data, error } = await requireClient()
    .from("messages")
    .select("*")
    .eq("conversation_id", conversationId)
    .order("created_at", { ascending: true });
  if (error) throw error;
  return (data || []).map(mapMessage);
}
